import { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ open, onClose, selectedService, initialData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    address: initialData?.address || '',
    service_id: selectedService?.id || '',
    service_name: selectedService?.name || '',
    booking_time: '10:00',
    location_type: 'home',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchServices();
      if (initialData?.address) {
        setFormData(prev => ({ ...prev, address: initialData.address }));
      }
      if (selectedService) {
        setFormData(prev => ({
          ...prev,
          service_id: selectedService.id,
          service_name: selectedService.name
        }));
      }
    }
  }, [open, initialData, selectedService]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'service_id') {
      const service = services.find(s => s.id === value);
      if (service) {
        setFormData(prev => ({ ...prev, service_name: service.name }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        booking_date: format(date, 'yyyy-MM-dd')
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking confirmed! Your barber will arrive shortly.');
      
      // Notify parent component about successful booking
      if (onSuccess) {
        onSuccess(response.data.id);
      }
      
      onClose();
      // Reset form
      setFormData({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        address: '',
        service_id: '',
        service_name: '',
        booking_time: '10:00',
        location_type: 'home',
        notes: ''
      });
      setDate(new Date());
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
    '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  return (
    <div data-testid="booking-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-b border-[var(--border-color)] p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[var(--text-primary)]" data-testid="booking-modal-title">Book Your Barber</h2>
          <button
            onClick={onClose}
            data-testid="close-booking-modal"
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Details */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="customer_name" className="text-[var(--text-primary)]">Full Name *</Label>
              <Input
                id="customer_name"
                data-testid="booking-name-input"
                required
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
              />
            </div>

            <div>
              <Label htmlFor="customer_phone" className="text-[var(--text-primary)]">Phone Number *</Label>
              <Input
                id="customer_phone"
                data-testid="booking-phone-input"
                required
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleChange('customer_phone', e.target.value)}
                className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
              />
            </div>

            <div>
              <Label htmlFor="customer_email" className="text-[var(--text-primary)]">Email (Optional)</Label>
              <Input
                id="customer_email"
                data-testid="booking-email-input"
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleChange('customer_email', e.target.value)}
                className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="location_type" className="text-[var(--text-primary)]">Location Type *</Label>
              <Select value={formData.location_type} onValueChange={(value) => handleChange('location_type', value)}>
                <SelectTrigger data-testid="booking-location-type-select" className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-[var(--border-color)] text-[var(--text-primary)]">
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="address" className="text-[var(--text-primary)]">Address *</Label>
              <Textarea
                id="address"
                data-testid="booking-address-input"
                required
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                rows={2}
              />
            </div>
          </div>

          {/* Service & Date/Time */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="service" className="text-[var(--text-primary)]">Service *</Label>
              <Select value={formData.service_id} onValueChange={(value) => handleChange('service_id', value)}>
                <SelectTrigger data-testid="booking-service-select" className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)]">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-[var(--border-color)] text-[var(--text-primary)]">
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} - ₦{service.price?.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--text-primary)]">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="booking-date-picker"
                      variant="outline"
                      className="w-full mt-1 justify-start text-left font-normal bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--bg-primary)] hover:border-[#00C167]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-[var(--border-color)]">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(newDate) => newDate && setDate(newDate)}
                      initialFocus
                      className="text-[var(--text-primary)]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="time" className="text-[var(--text-primary)]">Time *</Label>
                <Select value={formData.booking_time} onValueChange={(value) => handleChange('booking_time', value)}>
                  <SelectTrigger data-testid="booking-time-select" className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-[var(--border-color)] text-[var(--text-primary)] max-h-60">
                    {timeSlots.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-[var(--text-primary)]">Special Requests (Optional)</Label>
            <Textarea
              id="notes"
              data-testid="booking-notes-input"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
              rows={3}
              placeholder="Any specific requirements or instructions..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-booking-button"
              className="flex-1 border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--bg-primary)]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="confirm-booking-button"
              className="flex-1 bg-[#00C167] hover:bg-[#009E54] text-white"
            >
              {loading ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;